import type { BBStates, StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import { COMPONENT_STATES } from 'common/constants';
import { getComputedValue } from 'common/utils/get-computed-component-props';
import { DEFAULT_MEDIA_QUERY_PLATFORMS } from 'common/constants/story-media-query';
import { getMediaKeysInOrderOfInheritance } from 'common/utils/get-media-keys-in-order-of-inheritance';
import { getPlatformReferenceWidth, orderMediaQueryConfig } from 'common/utils/story-media-query';
import { convertPxToVw } from 'utils/helpers';
import {
	TEXT_PRESET_PROPERTIES,
	type DesignDefinition,
	type DesignModeSelection,
	type DesignToken,
	type TextPreset,
	type TextPresetProperty,
	type TextPresetPropertyValue,
	type TextPresetValue,
} from './types';
import { copyDesignDefinition } from './operations';
import { isDesignTokenTypeCompatible } from './properties';
import { normalizeDesignTokenValue } from './values';
import { resolveDesignModeValue, getDefaultDesignModeSelection } from './modes';

export const TEXT_PRESET_ENUM_VALUES = {
	textTransform: ['capitalize', 'uppercase', 'lowercase', 'none'],
	textDecoration: ['none', 'underline', 'overline', 'line-through'],
} as const;
const REFERENCE = /^var\(--sc-token-([a-z][a-z0-9-]*)\)$/;
const PLATFORMS = Object.values(DEFAULT_MEDIA_QUERY_PLATFORMS).filter(value => value !== 'default');
export function getTextPresetTokenId(value: unknown): string | undefined {
	return typeof value === 'string' ? value.match(REFERENCE)?.[1] : undefined;
}
export function normalizeTextPresetValue(property: TextPresetProperty, value: unknown): TextPresetValue {
	if (!TEXT_PRESET_PROPERTIES.includes(property)) throw new Error(`Unsupported text preset property: ${property}.`);
	if (getTextPresetTokenId(value)) {
		if (!(property in TEXT_PRESET_ENUM_VALUES)) return value as string;
		throw new Error(`Tokens are not supported for ${property}.`);
	}
	if (property in TEXT_PRESET_ENUM_VALUES) {
		const values: readonly string[] = TEXT_PRESET_ENUM_VALUES[property as keyof typeof TEXT_PRESET_ENUM_VALUES];
		if (typeof value !== 'string' || !values.includes(value)) throw new Error(`Invalid ${property} value.`);
		return value;
	}
	if (property === 'lineHeight' && typeof value === 'number') return normalizeDesignTokenValue('number', value);
	if (property === 'fontWeight') {
		const weight =
			typeof value === 'string' && /^(normal|bold)$/.test(value) ? (value === 'bold' ? 700 : 400) : value;
		const number = typeof weight === 'string' && /^\d+(\.\d+)?$/.test(weight) ? Number(weight) : weight;
		const normalized = normalizeDesignTokenValue('number', number);
		if (normalized < 1 || normalized > 1000) throw new Error('Font weight must be between 1 and 1000.');
		return normalized;
	}
	const type =
		property === 'fontFamily'
			? 'fontFamily'
			: property === 'color' || property === 'backgroundColor'
				? 'color'
				: 'dimension';
	return normalizeDesignTokenValue(type, value);
}
export function validateTextPresets(input: unknown): TextPreset[] {
	if (!Array.isArray(input)) throw new Error('Text presets require an array.');
	const ids = new Set<string>();
	return input.map((preset: TextPreset) => {
		if (
			!preset ||
			typeof preset.id !== 'string' ||
			!/^[a-z][a-z0-9-]*$/.test(preset.id) ||
			ids.has(preset.id) ||
			typeof preset.name !== 'string' ||
			!preset.name.trim() ||
			(preset.description !== undefined && typeof preset.description !== 'string') ||
			!preset.properties ||
			typeof preset.properties !== 'object' ||
			Array.isArray(preset.properties)
		)
			throw new Error('Text presets require unique valid IDs, names and properties.');
		ids.add(preset.id);
		const properties: TextPreset['properties'] = {};
		for (const [key, assignment] of Object.entries(preset.properties)) {
			const property = key as TextPresetProperty;
			if (
				!TEXT_PRESET_PROPERTIES.includes(property) ||
				!assignment ||
				typeof assignment !== 'object' ||
				Array.isArray(assignment) ||
				Object.keys(assignment).some(item => item !== 'base' && item !== 'platforms')
			)
				throw new Error(`Invalid text preset property: ${key}.`);
			const next: TextPresetPropertyValue = {};
			if (assignment.base !== undefined) next.base = normalizeTextPresetValue(property, assignment.base);
			if (assignment.platforms !== undefined) {
				if (
					!assignment.platforms ||
					typeof assignment.platforms !== 'object' ||
					Array.isArray(assignment.platforms)
				)
					throw new Error(`Invalid platforms for ${key}.`);
				const platforms: TextPresetPropertyValue['platforms'] = {};
				for (const [platform, value] of Object.entries(assignment.platforms)) {
					if (!PLATFORMS.includes(platform as StoryDefaultPlatform))
						throw new Error(`Invalid text preset platform: ${platform}.`);
					platforms[platform as StoryDefaultPlatform] = normalizeTextPresetValue(property, value);
				}
				if (Object.keys(platforms).length) next.platforms = platforms;
			}
			if (next.base === undefined && !Object.keys(next.platforms ?? {}).length)
				throw new Error(`${key} requires at least one assignment.`);
			properties[property] = next;
		}
		return {
			id: preset.id,
			name: preset.name,
			...(preset.description !== undefined ? { description: preset.description } : {}),
			properties,
		};
	});
}
export type TextPresetStyles = Record<string, Record<string, Record<string, unknown>>>;

/** Shared authored px assignment: convert only the authored platform, exactly once. */
export function normalizePlatformStyleValue(
	property: string,
	value: unknown,
	mediaQuery: StoryMediaQuery,
	platform: StoryDefaultPlatform
): unknown {
	return typeof value === 'string' &&
		/^[-+]?(?:\d+(?:\.\d+)?|\.\d+)px$/i.test(value.trim()) &&
		isDesignTokenTypeCompatible(property, 'dimension')
		? convertPxToVw(value, 2, getPlatformReferenceWidth(mediaQuery, platform))
		: value;
}
export function getTextPresetInheritedAssignment(
	assignment: TextPresetPropertyValue | undefined,
	platform: StoryDefaultPlatform,
	mediaQuery: StoryMediaQuery
): { value: TextPresetValue; source: string } | undefined {
	if (!assignment) return undefined;
	if (!mediaQuery.config[platform]) {
		const value = assignment.platforms?.[platform];
		return value === undefined ? undefined : { value, source: platform };
	}
	const keys = getMediaKeysInOrderOfInheritance(
		mediaQuery.defaultPlatform,
		mediaQuery.config[platform],
		orderMediaQueryConfig(mediaQuery, { defaultFirst: true, minOrder: 'asc', maxOrder: 'desc' })
	);
	let inherited: ReturnType<typeof getTextPresetInheritedAssignment>;
	for (const key of keys) {
		if (key === mediaQuery.defaultPlatform && assignment.base !== undefined)
			inherited = { value: assignment.base, source: 'Base' };
		const value = assignment.platforms?.[key as StoryDefaultPlatform];
		if (value !== undefined) inherited = { value, source: key };
	}
	return inherited;
}
function presetTokens(design: DesignDefinition): Map<string, DesignToken> {
	return new Map(
		design.tokens.collections.flatMap(collection => collection.tokens.map(token => [token.id, token] as const))
	);
}
export function getTextPresetDependencies(preset: TextPreset): string[] {
	return [
		...new Set(
			Object.values(preset.properties)
				.flatMap(assignment => [assignment.base, ...Object.values(assignment.platforms ?? {})])
				.map(getTextPresetTokenId)
				.filter((id): id is string => !!id)
		),
	];
}
export function importTextPreset(
	story: DesignDefinition,
	library: DesignDefinition,
	presetId: string
): DesignDefinition {
	const next = copyDesignDefinition(story);
	const local = next.textPresets?.find(preset => preset.id === presetId);
	const sourcePreset = library.textPresets?.find(preset => preset.id === presetId);
	if (!sourcePreset) throw new Error(`Unknown library text preset: ${presetId}.`);
	// Import dependencies of the selected library preset, even if a local version already exists.
	const preset = validateTextPresets([sourcePreset])[0];
	if (!local) next.textPresets = [...(next.textPresets ?? []), preset];
	const tokens = presetTokens(next);
	for (const id of getTextPresetDependencies(preset)) {
		const collection = library.tokens.collections.find(item => item.tokens.some(token => token.id === id));
		const source = collection?.tokens.find(token => token.id === id);
		const existing = tokens.get(id);
		if (existing) {
			if (source && source.type !== existing.type) throw new Error(`Token type conflict: ${id}.`);
			continue;
		}
		if (!source || !collection) throw new Error(`Missing text preset dependency: ${id}.`);
		let target = next.tokens.collections.find(item => item.id === collection.id);
		if (!target) {
			target = { id: collection.id, name: collection.name, tokens: [] };
			next.tokens.collections.push(target);
		}
		const token = structuredClone(source);
		target.tokens.push(token);
		tokens.set(id, token);
	}
	// Reject imported mode conditions against Story groups, without importing new mode groups.
	return copyDesignDefinition(next);
}
export function planTextPresetApplication({
	design,
	presetId,
	styles,
	mediaQuery,
	supportedProperties,
	validateFont,
}: {
	design: DesignDefinition | undefined;
	presetId: string;
	styles: TextPresetStyles;
	mediaQuery: StoryMediaQuery;
	supportedProperties: readonly string[];
	validateFont: (family: string, weight?: number) => void;
}): TextPresetStyles {
	const source = design?.textPresets?.find(item => item.id === presetId);
	if (!source || !design) throw new Error(`Unknown Story text preset: ${presetId}.`);
	const preset = validateTextPresets([source])[0];
	if (!Object.keys(preset.properties).length) throw new Error('An empty text preset cannot be applied.');
	const tokens = presetTokens(design);
	const assignments: Record<string, Record<string, unknown>> = {};
	for (const [property, value] of Object.entries(preset.properties)) {
		if (!supportedProperties.includes(property)) throw new Error(`This element does not support ${property}.`);
		const mapped = {
			...(value.base !== undefined ? { [mediaQuery.defaultPlatform]: value.base } : {}),
			...value.platforms,
		};
		for (const [platform, literal] of Object.entries(mapped)) {
			if (!mediaQuery.config[platform])
				throw new Error(`Story does not support text preset platform ${platform}.`);
			const id = getTextPresetTokenId(literal);
			if (id) {
				const token = tokens.get(id);
				if (!token) throw new Error(`Unresolved text preset token: ${id}.`);
				if (!isDesignTokenTypeCompatible(property, token.type))
					throw new Error(`Token ${id} is incompatible with ${property}.`);
				for (const tokenValue of [token.value.base, ...(token.value.overrides ?? []).map(item => item.value)])
					normalizeTextPresetValue(property as TextPresetProperty, tokenValue);
			}
			assignments[platform] ??= {};
			assignments[platform][property] = normalizePlatformStyleValue(
				property,
				literal,
				mediaQuery,
				platform as StoryDefaultPlatform
			);
		}
	}
	const next = structuredClone(styles);
	next.defaultState ??= {};
	for (const platform of Object.keys(next.defaultState)) {
		for (const property of Object.keys(preset.properties)) delete next.defaultState[platform][property];
	}
	for (const [platform, values] of Object.entries(assignments))
		next.defaultState[platform] = { ...next.defaultState[platform], ...values };
	// All possible mode combinations, including multi-group overrides, use the existing token resolver.
	const selections = design.modeGroups.reduce<DesignModeSelection[]>(
		(current, group) =>
			current.flatMap(selection => group.modes.map(mode => ({ ...selection, [group.id]: mode.id }))),
		[{}]
	);
	const resolve = (value: unknown, selection: DesignModeSelection): unknown => {
		const id = getTextPresetTokenId(value);
		const token = id ? tokens.get(id) : undefined;
		return token ? resolveDesignModeValue<string | number>(token.value, selection) : value;
	};
	if (preset.properties.fontFamily || preset.properties.fontWeight) {
		const ordered = orderMediaQueryConfig(mediaQuery, { defaultFirst: true, minOrder: 'asc', maxOrder: 'desc' });
		for (const platform of Object.keys(mediaQuery.config)) {
			const keys = getMediaKeysInOrderOfInheritance(
				mediaQuery.defaultPlatform,
				mediaQuery.config[platform],
				ordered
			);
			for (const state of Object.values(COMPONENT_STATES)) {
				const inherited = getComputedValue({
					source: next,
					sourceKey: 'styles',
					mediaKeysInOrderOfInheritance: keys,
					currState: state as BBStates,
				}).value as Record<string, unknown>;

				for (const selection of selections) {
					const family =
						resolve(inherited.fontFamily, selection) ??
						resolveDesignModeValue(design.defaults.root.fontFamily, selection);
					const weight = resolve(inherited.fontWeight, selection);
					validateFont(
						String(family),
						weight === undefined
							? undefined
							: Number(weight === 'normal' ? 400 : weight === 'bold' ? 700 : weight)
					);
				}
			}
		}
	}
	return next;
}
export function compileTextPresetsForAI(design: DesignDefinition) {
	const tokens = presetTokens(design);
	return (design.textPresets ?? []).map(preset => ({
		...structuredClone(preset),
		resolvedPreview: Object.fromEntries(
			Object.entries(preset.properties).map(([property, assignment]) => {
				const resolve = (value: TextPresetValue) => {
					const token = tokens.get(getTextPresetTokenId(value) ?? '');
					return token
						? resolveDesignModeValue<string | number>(
								token.value,
								getDefaultDesignModeSelection(design.modeGroups)
							)
						: value;
				};
				return [
					property,
					{
						...(assignment.base !== undefined ? { base: resolve(assignment.base) } : {}),
						...(assignment.platforms
							? {
									platforms: Object.fromEntries(
										Object.entries(assignment.platforms).map(([platform, value]) => [
											platform,
											resolve(value),
										])
									),
								}
							: {}),
					},
				];
			})
		),
		dependencies: getTextPresetDependencies(preset).map(id => {
			const token = tokens.get(id);
			return {
				id,
				reference: `var(--sc-token-${id})`,
				...(token ? { type: token.type, value: token.value } : { unresolved: true }),
			};
		}),
	}));
}
